import axios from "axios";

// Resolve the backend URL once at module load. The frontend MUST be built
// with REACT_APP_BACKEND_URL set; otherwise template-string coercion turns
// undefined into the literal string "undefined" and every API call silently
// rewrites to capacitor://localhost/undefined/api/... which dies inside the
// WKWebView with no useful error.
const RAW_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const HAS_BACKEND_URL =
  typeof RAW_BACKEND_URL === "string" &&
  RAW_BACKEND_URL.length > 0 &&
  RAW_BACKEND_URL !== "undefined";

// Last-resort fallback so a misbuilt bundle does not silently fail. For
// web builds we use the page's own origin (API calls go to the same host
// the bundle was served from — works in any deployment environment).
// For Capacitor native builds REACT_APP_BACKEND_URL MUST be baked in at
// build time; if it's missing the origin is capacitor://localhost and the
// app will fail loudly, which is what we want.
const FALLBACK_BACKEND_URL =
  typeof window !== "undefined" && window.location && window.location.origin
    ? window.location.origin
    : "";
const BACKEND_URL = HAS_BACKEND_URL ? RAW_BACKEND_URL : FALLBACK_BACKEND_URL;

if (!HAS_BACKEND_URL && typeof window !== "undefined") {
  // eslint-disable-next-line no-console
  console.warn(
    `[api] REACT_APP_BACKEND_URL was missing from the bundle. ` +
    `Falling back to ${FALLBACK_BACKEND_URL || "(empty)"}. Add ` +
    `frontend/.env.production with REACT_APP_BACKEND_URL=<your-host> and rebuild.`
  );
}

export const API = `${BACKEND_URL}/api`;

// Per-device anonymous identifier (used until/unless user signs up)
function getDeviceId() {
  let id = localStorage.getItem("ss_device_id");
  if (!id) {
    id =
      (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
      `dev-${Math.random().toString(36).slice(2)}-${Date.now()}`;
    localStorage.setItem("ss_device_id", id);
  }
  return id;
}

export const api = axios.create({ baseURL: API, timeout: 20000 });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("ss_token");
  config.headers["X-Device-Id"] = getDeviceId();
  if (token) config.headers["Authorization"] = `Bearer ${token}`;
  return config;
});

// Normalise FastAPI validation errors so callers can pass `err.response.data.detail`
// straight into a toast or JSX child without crashing React. FastAPI returns:
//   { detail: [ { type, loc, msg, input, url, ... }, ... ] }  for 422s
//   { detail: "Some string" }                                  for 4xx/5xx custom
// Without this, the array shape rendered as a React child throws
// "Objects are not valid as a React child (found: object with keys ...)".
api.interceptors.response.use(
  (resp) => resp,
  (error) => {
    const detail = error?.response?.data?.detail;
    if (Array.isArray(detail)) {
      const flat = detail
        .map((d) => (typeof d === "string" ? d : d?.msg || JSON.stringify(d)))
        .filter(Boolean)
        .join("; ");
      // Mutate in place so existing callers reading err.response.data.detail
      // continue to work — they now get a human-readable string.
      error.response.data.detail = flat || "Validation error";
    } else if (detail && typeof detail === "object") {
      error.response.data.detail = detail.msg || JSON.stringify(detail);
    }
    return Promise.reject(error);
  }
);

export function setToken(token) {
  if (token) localStorage.setItem("ss_token", token);
  else localStorage.removeItem("ss_token");
}

export function getToken() {
  return localStorage.getItem("ss_token");
}
